import { FC, useCallback, useMemo } from "react"
import { useField, ErrorMessageProps, useFormikContext } from "formik"
import {
  AlertIcon,
  TextField as MarmaladeTextField,
  TextFieldProps as MarmaladeTextFieldProps,
  SuccessIcon
} from "@eventbrite/marmalade"

export type HandleChange = (
  value: string,
  name: string,
  event: React.ChangeEvent<HTMLInputElement>
) => void

type ValidationAdornment = {
  success: JSX.Element
  error: JSX.Element
  none: null
}

export interface Props extends MarmaladeTextFieldProps {
  name: string
  errorMessageProps?: ErrorMessageProps
  required?: boolean
}

export const TextField: FC<Props> = ({
  name,
  label,
  helperText,
  type: t,
  required = false,
  ...otherProps
}) => {
  const [field, meta, helpers] = useField(name);
  const { submitCount } = useFormikContext();

  const type = useMemo(() => t ? t : "text", [t]);

  const validationAdornment = useMemo<ValidationAdornment>(
    () => ({
      success: <SuccessIcon size="small" />,
      error: <AlertIcon size="small" />,
      none: null
    }),
    []
  );

  const handleChange: HandleChange = useCallback(
    (value, name, event) => {
      if (otherProps?.onChange) {
        otherProps.onChange(value, name, event);
      } else {
        helpers.setValue(value, true);
      }
      helpers.setTouched(true, true);
    },
    [helpers, otherProps?.onChange]
  );

  const handleBlur = useCallback(() => {
    helpers.setTouched(true, true);
  }, [helpers]);

  const showError = meta.touched || submitCount > 0;
  
  // Only show validation adornment if the field is required
  const showValidationAdornment = required && (showError ? 
    meta.error ? 
      validationAdornment.error : 
      meta.value ? 
        validationAdornment.success : 
        validationAdornment.none
    : validationAdornment.none);

  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <MarmaladeTextField
        {...field}
        {...otherProps}
        label={label}
        name={name}
        value={meta.value || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        hasError={showError && !!meta.error}
        validationAdornment={showValidationAdornment}
        helperText={showError && meta.error ? meta.error : helperText}
        type={type}
        required={required}
      />
    </div>
  );
};